<script setup>
  import { ref, computed, nextTick } from "vue";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Trash2, X, Plus } from "lucide-vue-next";
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogClose,
  } from "@/components/ui/dialog";
  import {
    FEE_FIELD_MAP,
    calculateFeeAutoTotal,
    calculateFeeManualTotal,
    getManualField,
    getAutoField,
    getActiveTargets,
  } from "@/utils/pricer/pricerHelpers";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";

  const props = defineProps({
    open: { type: Boolean, required: true },
    serviceFees: { type: Array, required: true },
    feeVisibility: { type: Object, required: true },
  });

  const emit = defineEmits(["update:open", "update:feeVisibility"]);
  const { project } = storeToRefs(usePricerProjectStore());

  const isAddingFee = ref(false);
  const newFeeType = ref("");
  const addFeeRowRef = ref(null);

  const setFeeVisibility = (key, visible) => {
    emit("update:feeVisibility", { ...props.feeVisibility, [key]: visible });
  };

  const visibleFees = computed(() => props.serviceFees.filter((fee) => fee.show));
  const availableFees = computed(() => {
    const used = new Set(visibleFees.value.map((f) => f.type));
    return Object.entries(FEE_FIELD_MAP)
      .filter(([type]) => !used.has(type))
      .map(([type, config]) => ({ value: type, label: config.label }));
  });

  const updateAllCountries = (key, value) => {
    const field = getManualField(key);
    getActiveTargets(project.value).forEach((target) => {
      target.countries?.forEach((country) => {
        country[field] = typeof value === "function" ? value({ project: project.value, target, country, key }) : value;
      });
    });
  };

  const updateFee = (key, value) => {
    const config = FEE_FIELD_MAP[key];
    if (!config) return;

    const manualField = getManualField(key);
    if (config.isMultiTargetsCountry) return updateAllCountries(key, value);

    return (project.value[manualField] = typeof value === "function" ? value({ project: project.value, key }) : value);
  };

  const addFee = (key) => {
    const config = FEE_FIELD_MAP[key];
    if (!config) return;

    updateFee(key, config.baseFee ?? null);
    setFeeVisibility(key, true);
  };

  const removeFee = (key) => {
    // Use 0 for translation to avoid watcher re-setting the value
    const config = FEE_FIELD_MAP[key];
    updateFee(key, config?.isMultiTargetsCountry ? 0 : null);
    setFeeVisibility(key, false);
  };

  const startAddingFee = async () => {
    isAddingFee.value = true;
    await nextTick();
    addFeeRowRef.value?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const handleFeeSelection = (key) => {
    if (!key) return;
    addFee(key);
    isAddingFee.value = false;
    newFeeType.value = "";
  };

  const getAutoValue = (fee, source) => {
    const autoTotal = calculateFeeAutoTotal(source, fee.type);
    return FEE_FIELD_MAP[fee.type]?.hasAutoValue && autoTotal ? `${autoTotal}€` : "";
  };

  const canRemove = (fee) => !FEE_FIELD_MAP[fee.type]?.hasAutoValue;
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent
      class="sm:max-w-[60%]"
      :show-close-button="false">
      <DialogClose class="absolute top-4 right-4 p-0.5 transition-colors rounded-md hover:bg-secondary">
        <Button
          variant="ghost"
          size="icon-sm">
          <X class="w-4 h-4" />
        </Button>
      </DialogClose>
      <DialogHeader>
        <DialogTitle class="text-center text-xl font-semibold">Service fees</DialogTitle>
        <DialogDescription class="sr-only">Manage service fees</DialogDescription>
      </DialogHeader>

      <div class="overflow-x-hidden">
        <!-- Header -->
        <div class="flex items-center py-2 border-b text-muted-foreground text-sm">
          <div class="flex-[2] pr-4">Type of fee</div>
          <div class="w-30 px-2 text-center">Auto</div>
          <div class="w-30 px-2 text-center">Manual</div>
          <div class="w-10 text-center"></div>
        </div>

        <div class="max-h-[400px] overflow-y-auto">
          <template
            v-for="fee in visibleFees"
            :key="fee.type">
            <div
              v-if="fee.isMultiTargetsCountry"
              class="border-b">
              <div class="flex items-center py-3">
                <div class="flex-[2] pr-4 font-semibold">{{ fee.label }}</div>
                <div class="w-30 px-2 text-center text-sm text-muted-foreground">
                  {{ getAutoValue(fee, project) }}
                </div>
                <div class="w-30 px-2 text-center text-sm text-muted-foreground">
                  {{ calculateFeeManualTotal(project, fee.type) }}€
                </div>
                <div class="w-10 text-center">
                  <Button
                    v-if="canRemove(fee)"
                    variant="ghost"
                    size="icon"
                    @click="removeFee(fee.type)">
                    <Trash2 />
                  </Button>
                </div>
              </div>
              <template
                v-for="target in getActiveTargets(project)"
                :key="target.id || target.token">
                <div
                  v-for="country in target.countries || []"
                  :key="country.token"
                  class="flex items-center py-2 pl-4 border-t">
                  <div class="flex-[2] pr-4 text-sm text-muted-foreground">
                    {{ target.name }} - {{ country.country }}
                  </div>
                  <div class="w-30 px-2">
                    <Input
                      v-if="FEE_FIELD_MAP[fee.type]?.hasAutoValue"
                      v-model="country[getAutoField(fee.type)]"
                      class="text-center text-muted-foreground border-dashed"
                      readonly />
                  </div>
                  <div class="w-30 px-2">
                    <Input
                      v-model="country[getManualField(fee.type)]"
                      class="text-center"
                      type="number"
                      :min="0" />
                  </div>
                  <div class="w-10"></div>
                </div>
              </template>
            </div>
            <div
              v-else
              class="border-b">
              <div class="flex items-center py-3">
                <div class="flex-[2] pr-4 font-medium">{{ fee.label }}</div>
                <div class="w-30 px-2">
                  <Input
                    v-if="FEE_FIELD_MAP[fee.type]?.hasAutoValue"
                    class="text-center text-muted-foreground border-dashed"
                    :model-value="project[getAutoField(fee.type)]"
                    readonly />
                </div>
                <div class="w-30 px-2">
                  <Input
                    v-model="project[getManualField(fee.type)]"
                    class="text-center"
                    type="number"
                    :min="0" />
                </div>
                <div class="w-10 text-center">
                  <Button
                    v-if="canRemove(fee)"
                    variant="ghost"
                    size="icon"
                    @click="removeFee(fee.type)">
                    <Trash2 />
                  </Button>
                </div>
              </div>
            </div>
          </template>
          <div
            v-if="isAddingFee"
            ref="addFeeRowRef"
            class="border-b">
            <div class="flex items-center py-3">
              <div class="flex-[2] pr-4">
                <Select
                  v-model="newFeeType"
                  :options="availableFees"
                  placeholder="Select fee type"
                  @update:model-value="handleFeeSelection" />
              </div>
              <div class="w-30 px-2"></div>
              <div class="w-10 text-center">
                <Button
                  variant="ghost"
                  size="icon"
                  @click="
                    isAddingFee = false;
                    newFeeType = '';
                  ">
                  <X />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 pt-4">
          <Button
            v-if="availableFees.length > 0"
            variant="outline"
            class="shadow-xs"
            :disabled="isAddingFee"
            @click="startAddingFee">
            <Plus />
            Add fee
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
